import './recipe-card.css'
import '../v-quantity/v-quantity.js'
import '../recipe-drawer/recipe-drawer.js'
import { formatMoney } from '../../scripts/lib/util.js'
import { productQualifiesForGwp } from '../../scripts/lib/gwp-helpers.js'
import { PRODUCT_PACKAGE_WEIGHT_LBS } from '#scripts/lib/constants.js'

class RecipeCard extends HTMLElement {
  #quantityEl = null
  #addButtonEl = null
  #removeButtonEl = null
  #recipeDrawer = null

  static observedAttributes = ['data-quantity', 'data-layout', 'data-available', 'data-readonly']

  constructor () {
    super()

    this.handleAddClick = this.handleAddClick.bind(this)
    this.handleRemoveClick = this.handleRemoveClick.bind(this)
    this.handleCardClick = this.handleCardClick.bind(this)
    this.handleAboutClick = this.handleAboutClick.bind(this)
    this.handleQuantityChange = this.handleQuantityChange.bind(this)
  }

  connectedCallback() {
    this.render()
    this.#applyMetafieldBackgroundColor()
    this.#cacheElements()
    this.setupEventListeners()
    this.#updateAvailability()
    this.#checkLocationBasedOOS()
  }

  disconnectedCallback() {
    this.removeEventListeners()

    if (this.#recipeDrawer) {
      this.#recipeDrawer.remove()
      this.#recipeDrawer = null
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-quantity' && newValue !== oldValue) {
      const quantity = parseInt(newValue || '0', 10)
      this.#updateState(quantity)
    }

    if (name === 'data-layout' && newValue !== oldValue) {
      this.#quantityEl = this.querySelector('v-quantity')
      this.#addButtonEl = this.querySelector('[data-add-button]')
      this.#removeButtonEl = this.querySelector('.recipe-card__remove-link')
      const quantity = parseInt(this.dataset.quantity || '0', 10)
      this.#updateState(quantity)
    }

    if (name === 'data-available' && oldValue !== null && newValue !== oldValue) {
      this.#updateAvailability()
    }
  }

  render() {
    if (this.querySelector(':scope > [data-ssr]')) return

    const {
      variantId,
      productTitle,
      productUrl,
      variantOption1,
      price,
      compareAtPrice,
      poundsPerPackage,
      featuredImageUrl,
      featuredImageAlt,
      secondaryImageUrl,
      isSpecial,
      showActions,
      showSpecialBadge,
      cardPerRowMobile,
      cardPerRowDesktop,
      layout,
      quantity
    } = this.dataset

    const metafieldBgColor = this.dataset.imageBgColor
    const bgColor = this.determineBackgroundColor(productTitle)
    const currentQuantity = parseInt(quantity || '0', 10)
    const isHorizontal = layout === 'horizontal'

    const imageSizes = isHorizontal
      ? '72px'
      : this.calculateSizes(cardPerRowMobile, cardPerRowDesktop)

    const bgClass = metafieldBgColor ? '' : bgColor

    const html = `
      <div class="recipe-card__wrapper v:flex v:flex-col v:gap-3">
        <a href="${productUrl}?variant=${variantId}" class="recipe-card__image-link v:block">
          <div class="recipe-card__figure ${bgClass} v:px-3 v:py-2 v:rounded-sm v:aspect-[177/142] v:m:px-8 v:m:aspect-[230/149] v:relative v:overflow-hidden">
            ${this.renderImages(featuredImageUrl, featuredImageAlt, secondaryImageUrl, imageSizes)}
            ${this.renderBadge(isSpecial, showSpecialBadge, productTitle)}
          </div>
        </a>

        <div class="recipe-card__content v:flex v:flex-col ${!isHorizontal && 'v:gap-1'}">
          ${variantOption1 ? `<p class="recipe-card__variant v:text-label-xs v:font-bold ${variantOption1.toLowerCase().includes('ground') ? 'v:text-ground' : 'v:text-chunked'}">${variantOption1}</p>` : ''}

          <div class="recipe-card__title-price v:flex v:flex-col v:gap-1 v:items-start v:m:flex-row v:m:gap-3 v:m:items-center v:m:justify-between">
            <h3 class="recipe-card__title v:text-body v:font-medium v:leading-none v:text-text-primary">
              <a href="${productUrl}?variant=${variantId}">${productTitle}</a>
            </h3>

            <p class="recipe-card__price v:shrink-0 v:whitespace-nowrap v:leading-none">
              ${isHorizontal ? this.renderPrice(price, compareAtPrice, 0) : this.renderPrice(price, compareAtPrice, poundsPerPackage)}
            </p>
          </div>
        </div>

        <div class="recipe-card__actions v:flex v:flex-col v:gap-1-5 v:items-center">
          ${isHorizontal
            ? this.renderHorizontalActions(productTitle, variantId, currentQuantity, showActions)
            : showActions === 'true' ? this.renderVerticalActions(productUrl, variantId) : ''
          }
        </div>
      </div>
    `

    this.innerHTML = html
  }

  #applyMetafieldBackgroundColor() {
    const metafieldColor = this.dataset.imageBgColor
    if (!metafieldColor) return

    const isValidHexColor = /^#[0-9A-F]{6}$/i.test(metafieldColor)
    if (!isValidHexColor) {
      console.warn(`[recipe-card] Invalid color format in metafield: ${metafieldColor}`)
      return
    }

    const figureEl = this.querySelector('.recipe-card__figure')
    if (figureEl) {
      figureEl.style.backgroundColor = metafieldColor
    }
  }

  renderHorizontalActions(productTitle, variantId, currentQuantity, showActions) {
    const isReadonly = this.dataset.readonly === 'true'

    if (isReadonly) {
      const lbs = currentQuantity * 2
      return `
        <div class="recipe-card__quantity-readonly v:flex v:gap-1 v:items-center v:text-text-primary">
          <span class="v:font-bold v:text-lg">${lbs}</span>
          <span class="v:text-sm"> lbs</span>
        </div>
      `
    }

    return `
      <v-quantity
        class="v-quantity v-quantity--compact v:flex v:items-center v:justify-center v:gap-2 v:rounded-sm"
        data-value="${currentQuantity}"
        data-variant-id="${variantId}"
        data-unit="lbs"
        data-min="0">
      </v-quantity>
      ${showActions !== 'false' ? `
        <button type="button"
          class="recipe-card__remove-link v:text-link-sm v:text-text-primary v:underline"
          aria-label="Remove ${productTitle} from cart">
          Remove
        </button>
      ` : ''}
    `
  }

  renderVerticalActions(productUrl, variantId) {
    return `
      <div class="recipe-card__state recipe-card__state--idle v:w-full">
        <button type="button"
          class="button button--primary v:button--xs v:w-full"
          data-add-button>
        </button>
      </div>

      <div class="recipe-card__state recipe-card__state--active v:w-full">
        <v-quantity
          class="v-quantity v:flex v:items-center v:justify-center v:gap-2 v:rounded-sm v:w-full"
          data-value="0"
          data-variant-id="${variantId}"
          data-unit="lbs"
          data-min="0">
        </v-quantity>
      </div>

      <a href="${productUrl}?variant=${variantId}" class="recipe-card__about-link v:text-link-sm v:text-text-brand1 v:underline">
        About Recipe
      </a>
    `
  }

  #cacheElements() {
    const quantity = parseInt(this.dataset.quantity || '0', 10)
    this.#quantityEl = this.querySelector('v-quantity')
    this.#addButtonEl = this.querySelector('[data-add-button]')
    this.#removeButtonEl = this.querySelector('.recipe-card__remove-link')

    this.#updateState(quantity)
  }

  #updateAvailability() {
    if (!this.#addButtonEl) return

    const isAvailable = this.dataset.available === 'true'
    const productTitle = this.dataset.productTitle

    if (isAvailable) {
      this.#addButtonEl.disabled = false
      this.#addButtonEl.textContent = 'Add'
      this.#addButtonEl.setAttribute('aria-label', `Add ${productTitle} to cart`)
    } else {
      this.#addButtonEl.disabled = true

      const restockDate = this.dataset.restockDate
      let buttonText = 'Out of Stock'
      if (restockDate) {
        const date = new Date(restockDate)
        const month = date.getMonth() + 1
        const day = date.getDate()
        buttonText = `Restocking ${month}/${day}`
      }
      this.#addButtonEl.textContent = buttonText
      this.#addButtonEl.setAttribute('aria-label', `${productTitle} is out of stock`)
    }
  }

  determineBackgroundColor(title) {
    const titleLower = (title || '').toLowerCase()

    if (titleLower.includes('puppies')) {
      return 'v:bg-recipe-puppies'
    }

    const bgMap = {
      'chicken': 'v:bg-recipe-chicken-light',
      'turkey': 'v:bg-recipe-turkey-light',
      'beef': 'v:bg-recipe-beef-light',
      'duck': 'v:bg-recipe-duck-light',
      'rabbit': 'v:bg-recipe-rabbit-light',
      'deer': 'v:bg-recipe-deer-light',
      'pork': 'v:bg-recipe-pork-light'
    }

    for (const [keyword, bgClass] of Object.entries(bgMap)) {
      if (titleLower.includes(keyword)) {
        return bgClass
      }
    }
    return 'v:bg-recipe-chicken-light'
  }

  calculateSizes(mobile, desktop) {
    const mobileCount = mobile || 2
    const desktopCount = desktop || 4
    const desktopMid = Math.min(3, desktopCount)

    return `(max-width: 699px) calc(100vw / ${mobileCount} - 40px), (max-width: 1200px) calc(100vw / ${desktopMid} - 64px), calc((100vw - 96px) / ${desktopCount} - (24px / ${desktopCount} * ${desktopCount - 1}))`
  }

  renderImages(featuredUrl, featuredAlt, secondaryUrl, sizes) {
    const widths = '72,144,200,300,400,500,600,700,800,1000,1200,1400,1600,1800'
    let html = ''

    if (featuredUrl) {
      const srcset = widths.split(',').map(w => {
        const width = parseInt(w)
        const url = featuredUrl.replace(/(\.\w+)(\?|$)/, `_${width}x$1$2`)
        return `${url} ${width}w`
      }).join(', ')

      html += `
        <img src="${featuredUrl}"
          srcset="${srcset}"
          sizes="${sizes}"
          loading="eager"
          fetchpriority="low"
          class="recipe-card__image recipe-card__image--primary v:w-full v:h-full v:object-contain"
          alt="${featuredAlt || ''}"
        />
      `
    }

    if (secondaryUrl) {
      const srcset = widths.split(',').map(w => {
        const width = parseInt(w)
        const url = secondaryUrl.replace(/(\.\w+)(\?|$)/, `_${width}x$1$2`)
        return `${url} ${width}w`
      }).join(', ')

      html += `
        <img src="${secondaryUrl}"
          srcset="${srcset}"
          sizes="${sizes}"
          loading="eager"
          fetchpriority="low"
          class="recipe-card__image recipe-card__image--secondary v:hidden v:w-full v:h-full v:object-contain"
          alt="${featuredAlt || ''}"
        />
      `
    }

    return html
  }

  renderPrice(price, compareAtPrice, poundsPerPackage) {
    const priceNum = parseInt(price || 0)
    const compareAtPriceNum = parseInt(compareAtPrice || 0)
    const poundsNum = parseFloat(poundsPerPackage || PRODUCT_PACKAGE_WEIGHT_LBS)

    const isOnSale = compareAtPriceNum > priceNum

    let priceDisplay = ''

    if (poundsNum > 0) {
      const pricePerPound = priceNum / poundsNum
      priceDisplay = formatMoney(pricePerPound) + ' / lb'

      if (isOnSale) {
        const comparePerPound = compareAtPriceNum / poundsNum
        return `
          <span class="v:text-label-sm v:text-text-primary">
            <span class="sr-only">Sale price</span>
            ${formatMoney(pricePerPound)} / lb
          </span>
          <span class="v:text-label-sm v:line-through v:text-text-idle">
            <span class="sr-only">Regular price</span>
            ${formatMoney(comparePerPound)} / lb
          </span>
        `
      } else {
        return `
          <span class="v:text-label-sm v:text-text-primary">
            ${priceDisplay}
          </span>
        `
      }
    } else {
      if (isOnSale) {
        return `
          <span class="v:text-label-sm v:text-text-primary">
            <span class="sr-only">Sale price</span>
            ${formatMoney(priceNum)}
          </span>
          <span class="v:text-label-sm v:line-through v:text-text-idle">
            <span class="sr-only">Regular price</span>
            ${formatMoney(compareAtPriceNum)}
          </span>
        `
      } else {
        return `
          <span class="v:text-label-sm v:text-text-primary">
            ${formatMoney(priceNum)}
          </span>
        `
      }
    }
  }

  renderBadge(isSpecial, showSpecialBadge, productTitle) {
    const shouldShow = showSpecialBadge !== 'false'
    if (!shouldShow) return ''

    const isSpecialProduct = isSpecial === 'true'

    if (isSpecialProduct) {
      const qualifiesForGift = productQualifiesForGwp(productTitle || '')
      const badgeText = qualifiesForGift ? 'Special • Get a FREE Gift' : 'Special'
      return `<p class="recipe-card__badge v:absolute v:bottom-0 v:left-0 v:right-0 v:border-t v:border-border-primary v:px-2 v:py-1 v:text-label-xs v:font-bold v:leading-none v:text-center v:[--recipe-card-badge-bg:var(--v-color-brand-purple)] v:[--recipe-card-badge-text:var(--v-color-text-invert)]">${badgeText}</p>`
    }

    const { customBadgeLabel, customBadgeBgColor, customBadgeTextColor } = this.dataset
    if (!customBadgeLabel) return ''

    const styleProps = [
      customBadgeBgColor && `--recipe-card-badge-bg: ${customBadgeBgColor}`,
      customBadgeTextColor && `--recipe-card-badge-text: ${customBadgeTextColor}`
    ].filter(Boolean).join('; ')
    const styleAttr = styleProps ? ` style="${styleProps}"` : ''

    return `<p class="recipe-card__badge v:absolute v:bottom-0 v:left-0 v:right-0 v:border-t v:border-border-primary v:px-2 v:py-1 v:text-label-xs v:font-bold v:leading-none v:text-center"${styleAttr}>${customBadgeLabel}</p>`
  }


  setupEventListeners() {
    if (this.#addButtonEl) {
      this.#addButtonEl.addEventListener('click', this.handleAddClick)
    }

    if (this.#removeButtonEl) {
      this.#removeButtonEl.addEventListener('click', this.handleRemoveClick)
    }

    if (this.dataset.showActions === 'true') {
      this.addEventListener('click', this.handleCardClick)
    }

    this.addEventListener('v-quantity:change', this.handleQuantityChange)
  }

  removeEventListeners() {
    if (this.#addButtonEl) {
      this.#addButtonEl.removeEventListener('click', this.handleAddClick)
    }

    if (this.#removeButtonEl) {
      this.#removeButtonEl.removeEventListener('click', this.handleRemoveClick)
    }

    if (this.dataset.showActions === 'true') {
      this.removeEventListener('click', this.handleCardClick)
    }

    this.removeEventListener('v-quantity:change', this.handleQuantityChange)
  }

  handleAddClick(event) {
    event.preventDefault()

    const currentQuantity = parseInt(this.dataset.quantity || '0', 10)
    const newQuantity = currentQuantity + 1
    this.dataset.quantity = newQuantity
    this.#updateState(newQuantity)

    this.#emitQuantityChange(newQuantity, currentQuantity)
  }

  handleRemoveClick(event) {
    event.preventDefault()

    const currentQuantity = parseInt(this.dataset.quantity || '0', 10)
    const newQuantity = 0
    this.dataset.quantity = newQuantity
    this.#updateState(newQuantity)

    this.#emitQuantityChange(newQuantity, currentQuantity)
  }

  handleCardClick(event) {
    const link = event.target.closest('a')
    if (link) {
      event.preventDefault()
      this.handleAboutClick(event)
    }
  }

  handleAboutClick(event) {
    event?.preventDefault()

    if (!this.#recipeDrawer) {
      this.#recipeDrawer = document.createElement('recipe-drawer')
      this.#recipeDrawer.id = `recipe-drawer-${this.dataset.variantId}`
      this.#recipeDrawer.classList.add('recipe-drawer', 'drawer', 'drawer--lg')
      this.#recipeDrawer.setAttribute('header-bordered', '')
      this.#recipeDrawer.dataset.productUrl = this.dataset.productUrl
      this.#recipeDrawer.dataset.title = this.dataset.productTitle
      document.body.appendChild(this.#recipeDrawer)

      requestAnimationFrame(() => {
        this.#recipeDrawer.show()
      })
    } else {
      this.#recipeDrawer.show()
    }
  }

  handleQuantityChange(event) {
    const newQuantity = event.detail.value
    const oldQuantity = event.detail.oldValue
    this.dataset.quantity = newQuantity
    this.#updateState(newQuantity)

    this.#emitQuantityChange(newQuantity, oldQuantity)
  }

  #updateState(quantity) {
    if (this.#quantityEl) {
      this.#quantityEl.dataset.value = quantity
    }

    this.dataset.state = quantity === 0 ? 'idle' : 'active'
  }

  #emitQuantityChange(newQuantity, oldQuantity) {
    const variantId = this.dataset.variantId

    this.dispatchEvent(new CustomEvent('recipe-card:quantity-change', {
      detail: {
        variantId,
        newQuantity,
        oldQuantity
      },
      bubbles: true
    }))
  }

  #checkLocationBasedOOS() {
    if (this.dataset.available !== 'true') return

    window.VIVA?.is_oos?.(this.dataset.variantId)?.then(isOOS => {
      if (isOOS) {
        const currentQuantity = parseInt(this.dataset.quantity || '0', 10)

        this.dataset.available = 'false'
        this.dataset.quantity = '0'

        if (currentQuantity > 0) {
          this.#emitQuantityChange(0, currentQuantity)
        }
      }
    }).catch(err => {
      console.error('OOS check failed for variant', this.dataset.variantId, err)
    })
  }

  get productId() {
    return this.dataset.productId
  }

  get price() {
    return parseInt(this.dataset.price || '0', 10)
  }

  get compareAtPrice() {
    return parseInt(this.dataset.compareAtPrice || '0', 10)
  }
}

if (!customElements.get('recipe-card')) {
  customElements.define('recipe-card', RecipeCard)
}